import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  Stack,
  Text,
} from '@eluve/components';

import { getOutputTemplatesForOverrideQuery } from '../operations';

import { OverrideSection } from './OverrideSection';
import { VariantConfig } from './types';

interface OutputTemplateOverrideProps {
  config: VariantConfig;
  onChange: (updates: Partial<VariantConfig>) => void;
}

export const OutputTemplateOverride: React.FC<OutputTemplateOverrideProps> = ({
  config,
  onChange,
}) => {
  const { data } = useQuery(getOutputTemplatesForOverrideQuery);

  const hasValues =
    config.OUTPUT_TEMPLATE_ID !== undefined ||
    config.OUTPUT_TEMPLATE_VARIANT_ID !== undefined;

  const [isExpanded, setIsExpanded] = useState(hasValues);

  const templates = useMemo(() => {
    return data?.llmOutputTemplates ?? [];
  }, [data?.llmOutputTemplates]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === config.OUTPUT_TEMPLATE_ID);
  }, [templates, config.OUTPUT_TEMPLATE_ID]);

  const variants = useMemo(() => {
    return selectedTemplate?.variants ?? [];
  }, [selectedTemplate]);

  useEffect(() => {
    if (config.OUTPUT_TEMPLATE_ID && !config.OUTPUT_TEMPLATE_VARIANT_ID) {
      const defaultVariant = variants.find((v) => v.isCurrentDefault);
      if (defaultVariant) {
        onChange({
          OUTPUT_TEMPLATE_VARIANT_ID: `${defaultVariant.outputTemplateId}:${defaultVariant.variantNumber}`,
        });
      }
    }
  }, [
    config.OUTPUT_TEMPLATE_ID,
    config.OUTPUT_TEMPLATE_VARIANT_ID,
    variants,
    onChange,
  ]);

  const handleEnabledChange = (enabled: boolean) => {
    setIsExpanded(enabled);
    if (!enabled) {
      onChange({
        OUTPUT_TEMPLATE_ID: undefined,
        OUTPUT_TEMPLATE_VARIANT_ID: undefined,
      });
    }
  };

  const handleTemplateChange = (templateId: string) => {
    onChange({
      OUTPUT_TEMPLATE_ID: templateId,
      OUTPUT_TEMPLATE_VARIANT_ID: undefined,
    });
  };

  const selectedVariantKey = config.OUTPUT_TEMPLATE_VARIANT_ID;
  const selectedVariant = variants.find(
    (v) => `${v.outputTemplateId}:${v.variantNumber}` === selectedVariantKey,
  );

  return (
    <OverrideSection
      label="Override Output Template"
      enabled={isExpanded}
      onEnabledChange={handleEnabledChange}
    >
      <Stack gap={3}>
        <Stack gap={1}>
          <Text size="xs" color="secondary">
            Template
          </Text>
          <Combobox>
            <ComboboxSelectButton className="w-full">
              {selectedTemplate
                ? `${selectedTemplate.name}${selectedTemplate.llmOutputType ? ` (${selectedTemplate.llmOutputType})` : ''}`
                : 'Select template'}
            </ComboboxSelectButton>
            <ComboboxDropdown searchPlaceholder="Search templates...">
              <ComboboxEmpty>No templates found</ComboboxEmpty>
              {templates.map((template) => (
                <ComboboxOption
                  key={template.id}
                  onSelect={() => handleTemplateChange(template.id)}
                  searchValue={`${template.name} ${template.llmOutputType ?? ''} ${template.id}`}
                >
                  <Stack justify="between" gap={2} direction="horizontal">
                    <Text size="md" inline>
                      {template.name}
                      {template.llmOutputType && (
                        <Text
                          inline
                          size="xs"
                          color="tertiary"
                          className="ml-1"
                        >
                          ({template.llmOutputType})
                        </Text>
                      )}
                    </Text>
                    <Text
                      inline
                      size="xs"
                      className="font-mono"
                      color="tertiary"
                    >
                      {template.id.slice(0, 8)}
                    </Text>
                  </Stack>
                </ComboboxOption>
              ))}
            </ComboboxDropdown>
          </Combobox>
        </Stack>

        {config.OUTPUT_TEMPLATE_ID && variants.length > 0 && (
          <Stack gap={1}>
            <Text size="xs" color="secondary">
              Variant
            </Text>
            <Combobox>
              <ComboboxSelectButton className="w-full">
                {selectedVariant
                  ? `v${selectedVariant.variantNumber}${selectedVariant.isCurrentDefault ? ' (default)' : ''}`
                  : 'Select variant'}
              </ComboboxSelectButton>
              <ComboboxDropdown searchPlaceholder="Search variants...">
                <ComboboxEmpty>No variants found</ComboboxEmpty>
                {variants.map((variant) => {
                  const key = `${variant.outputTemplateId}:${variant.variantNumber}`;
                  return (
                    <ComboboxOption
                      key={key}
                      onSelect={() =>
                        onChange({ OUTPUT_TEMPLATE_VARIANT_ID: key })
                      }
                      searchValue={`v${variant.variantNumber}`}
                    >
                      v{variant.variantNumber}
                      {variant.isCurrentDefault ? ' (default)' : ''}
                    </ComboboxOption>
                  );
                })}
              </ComboboxDropdown>
            </Combobox>
          </Stack>
        )}
      </Stack>
    </OverrideSection>
  );
};
